import { useCallback, useEffect, useState } from "react"
import { FiPlus } from "react-icons/fi";
import { FaTrashAlt } from "react-icons/fa";
import axios, { AxiosResponse } from "axios";

import { API_URL } from "@redux/api";

import { InventoryItemLoading, NotificationType } from "react-app-env";

import SelectInput from "@components/form/SelectInput";
import { TextInputDefault } from "@components/form/TextInput";
import { validateAddInventories } from "@redux/validations/inventories";
import Notification from "@components/common/Notification";

type Props = {
  loadingId: string | undefined;
  recentInventories: InventoryItemLoading[] | [];
  onCloseModal: () => void;
};

type InventoryItem = {
  item: any;
  quantity: number | undefined;
  unit: string;
  trip: number;
};

type InventoriesStateType = InventoryItem[] | [];

type LineFormError = {
  name?: string | undefined;
  quantity?: string | undefined;
  unit?: string | undefined;
};

type ErrorsState = LineFormError[] | [];

export type AddInventoriesPayload = {
  item: any;
  quantity: number | undefined;
  unit: string;
  trip: number;
};

const AddNewInventories = ({
  loadingId,
  recentInventories,
  onCloseModal
}: Props) => {

  const [fetchLoading, setFetchLoading] = useState<boolean>(false);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);

  const [inventoriesOptions, setInventoriesOptions] = useState<any>([]);

  const [inventories, setInventories] = useState<InventoriesStateType>([]);
  const [errors, setErrors] = useState<ErrorsState>([]);
  const [notification, setNotification] = useState<NotificationType | null>(null);

  const fetchInventoriesOptions = useCallback(async() => {
    setFetchLoading(true);
    const response: AxiosResponse = await axios.get(`${API_URL}/inventory/options`);
    const { status } = response;
    if(status === 200) {
      const { data } = response.data;

      let optionsData = data;

      if(inventories) {
        const filterInventories = recentInventories.map((el) => el.item._id);
        optionsData = data.filter((el: {label: string, value:string}) => !filterInventories.includes(el.value));
      };

      setInventoriesOptions(optionsData);
      setFetchLoading(false);
    } else {
      setFetchLoading(false);
    };
    
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  useEffect(() => {
    fetchInventoriesOptions();

    const defaultErrors = [];
    const defaultInventories = [];
    
    if(inventories.length === 0) {
      defaultInventories.push({
        item: null,
        quantity: undefined,
        unit: "",
        trip: 0
      });

      defaultErrors.push({
        name: undefined,
        quantity: undefined,
        unit: undefined
      });
    };

    setInventories(defaultInventories);
    setErrors(defaultErrors);

    return () => {}

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const onAddNewLineForm = (e: any) => {
    e.preventDefault();
    const inventoriesValue = [...inventories, {
      item: null,
      quantity: undefined,
      unit: "",
      trip: 0
    }];

    const errorsValue = [...errors, {
      name: undefined,
      quantity: undefined,
      unit: undefined
    }];

    setInventories(inventoriesValue);
    setErrors(errorsValue);
  };

  const onRemoveLineForm = (e: any, idx: number) => {
    e.preventDefault();
    const newInventories = [...inventories];
    newInventories.splice(idx, 1);

    const newErrors = [...errors];
    errors.splice(idx, 1);

    setInventories(newInventories);
    setErrors(newErrors);
  };

  const onChangeError = ({target, value, idx}: {
    target: "name" | "quantity";
    value: string | undefined;
    idx: number;
  }) => {
    const newErrors = errors.map((item, sidx) => {
      if(idx === sidx) {
        return {
          ...item,
          [target]: value
        }
      };

      return item;
    });

    setErrors(newErrors);
  };

  const onChangeInput = (value: any, idx: number, targetName: string) => {
    onChangeError({
      target: targetName === 'item' ? "name" : "quantity",
      value: undefined,
      idx
    });

    const newInventories = inventories.map((inventory, sidx) => {
      if(idx !== sidx) return inventory;
      if(targetName === 'item') {
        return {
          ...inventory,
          unit: value.unit,
          [targetName]: value
        }
      };

      return {
        ...inventory,
        [targetName]: value
      }
    });

    setInventories(newInventories);
  };

  const isValid = (payload: AddInventoriesPayload[]) => {
    const { isValid, errors } = validateAddInventories(payload);
    if(errors.length > 0) setErrors(errors);
    return isValid;
  };

  const onSubmit = async(e: any) => {
    e.preventDefault();
    setNotification(null);
    setSubmitLoading(true);

    if(inventories.length === 0) return onCloseModal();

    if(isValid(inventories)) {
      const addedInventories = inventories.map(inv => {
        return {
          ...inv,
          item: inv.item.value,
        };
      });

      const payload = {
        inventories: addedInventories
      };
      
      const response: AxiosResponse = await axios.post(`${API_URL}/loading/${loadingId}/inventories/add`, payload);
      const { status, data } = response;

      if(status === 200) {
        setNotification({
          isError: false,
          title: "Sukses!",
          text: "Berhasil menambah barang"
        });
        
        setTimeout(() => {
          setSubmitLoading(false);
          onCloseModal();
        }, 2000);
      } else {
        setNotification({
          isError: false,
          title: "Sukses!",
          text: data.message
        });
        setSubmitLoading(false);
      };
    } else {
      setNotification({
        isError: true,
        title: "Error",
        text: "Mohon lengkapi form di atas dengan benar"
      });
      setSubmitLoading(false);
    };
  };

  return (
    <div>
      <form>
        {inventories.map((item, idx) => {
          return (
            <div key={`new-inventory-${idx}`} className="mt-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-sm font-semibold pb-1">Nama barang</label>
                  <SelectInput
                    isMulti={false}
                    label=""
                    // label={idx === 0 ? 'Nama barang' : ""}
                    placeholder="Pilih barang"
                    disabled={submitLoading || fetchLoading}
                    options={inventoriesOptions}
                    value={item.item}
                    isSearchable={true}
                    error={errors[idx]?.name}
                    onChange={(value) => {
                      onChangeInput(value, idx, "item");
                    }}
                  />
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-semibold pb-1">Qty {item.unit && `(${item.unit})`}</label>
                  <div className="flex items-center justify-center gap-3">
                    <TextInputDefault 
                      name={`quantity_${idx}`}
                      label=""
                      // label={idx === 0 ? 'Qty' : ""}
                      type="number"
                      value={item.quantity}
                      placeholder="Jumlah"
                      onChange={({ target }) => {
                        const { value } = target;
                        onChangeInput(value, idx, "quantity");
                      }}
                      disabled={submitLoading}
                      error={errors[idx]?.quantity}
                    />
                    { idx > 0 &&
                      <button className="text-red-500" onClick={(e) => onRemoveLineForm(e, idx)}>
                        <FaTrashAlt className="" />
                      </button>
                    }
                  </div>
                </div>
              </div>
            </div>
          )
        })}
        <button 
          className="bg-sky-500 text-white rounded-xl py-3 flex items-center justify-center w-full mt-4"
          onClick={onAddNewLineForm}
        >
          <FiPlus />
          Tambah barang lain
        </button>

        <hr className="mt-5 mb-8" />

        <Notification notification={notification} />

        <button 
          className="bg-orange-600 text-white rounded-xl py-3 flex items-center justify-center w-full mt-4"
          onClick={onSubmit}
        >
          Simpan
        </button>
      </form>
    </div>
  )
}

export default AddNewInventories